import React, { useRef, useState, useEffect } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import "./VideoReels.css";

const videos = [
  {
    src: "https://www.youtube.com/embed/xT6_3GG9C04",
    title: "#ModernPrem series",
    subtitle: "Real stories from our Modern Prem series.",
  },
  {
    src: "https://www.youtube.com/embed/vF7o78GYzn0",
    title: "USA Student Visa",
    subtitle: "Student visa success journeys for the USA.",
  },
  {
    src: "https://www.youtube.com/embed/FmijxTXB-WY",
    title: "IELTS Coaching",
    subtitle: "Boost your band score with expert coaching.",
  },
  {
    src: "https://www.youtube.com/embed/QKLSPLfrNVk",
    title: "IELTS Master Class",
    subtitle: "Master strategies that top scorers use.",
  },
  {
    src: "https://www.youtube.com/embed/VxH7405ZNBQ",
    title: "Study in Germany",
    subtitle: "Affordable, world-class education in Germany.",
  },
];

const reactionPool = ["❤️", "🔥", "✨", "👏", "😍"];

export default function VideoReels() {
  const reelRef = useRef(null);
  const modalIframeRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [reactions, setReactions] = useState([]);
  const [touchStartY, setTouchStartY] = useState(null);

  /* Auto-scroll (desktop only) */
  useEffect(() => {
    if (window.innerWidth < 768) return;

    const interval = setInterval(() => {
      reelRef.current?.scrollBy({ left: 260, behavior: "smooth" });
      setActiveIndex((prev) => (prev + 1) % videos.length);
    }, 4500);

    return () => clearInterval(interval);
  }, []);

  /* Modal handlers */
  const openModal = (index) => {
    setActiveIndex(index);
    setModalOpen(true);
    setTimeout(() => {
      modalIframeRef.current?.contentWindow?.postMessage(
        '{"event":"command","func":"playVideo","args":""}',
        "*"
      );
    }, 300);
  };

  const closeModal = () => {
    modalIframeRef.current?.contentWindow?.postMessage(
      '{"event":"command","func":"pauseVideo","args":""}',
      "*"
    );
    setModalOpen(false);
    setReactions([]);
  };

  /* Reactions */
  const addReaction = () => {
    const emoji =
      reactionPool[Math.floor(Math.random() * reactionPool.length)];
    setReactions((prev) => [
      ...prev,
      { id: Date.now(), emoji, x: Math.random() * 60 - 30 },
    ]);
  };

  /* Swipe navigation (modal) */
  const handleTouchStart = (e) =>
    e.touches?.length && setTouchStartY(e.touches[0].clientY);

  const handleTouchEnd = (e) => {
    if (!touchStartY) return;
    const delta = e.changedTouches[0].clientY - touchStartY;
    if (delta > 60)
      setActiveIndex((p) => (p === 0 ? videos.length - 1 : p - 1));
    if (delta < -60)
      setActiveIndex((p) => (p === videos.length - 1 ? 0 : p + 1));
    setTouchStartY(null);
  };

  return (
    <>
      <section className="video-reels">
        <h2 className="video-reels-title">
          They're living the dream, <span>and you can too!</span>
        </h2>

        {/* ACTIVE TITLE (important for mobile) */}
        <motion.div
          key={activeIndex}
          className="active-video-meta"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="active-video-title">
            {videos[activeIndex].title}
          </div>
          <div className="active-video-subtitle">
            {videos[activeIndex].subtitle}
          </div>
        </motion.div>

        <div className="carousel" ref={reelRef}>
          {videos.map((video, index) => (
            <div
              key={index}
              className={`video-card ${
                index === activeIndex ? "active" : ""
              }`}
              onClick={() => openModal(index)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <div className="glass-phone">
                <iframe
                  src={`${video.src}?enablejsapi=1`}
                  className="video"
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MODAL */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            className="reels-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="reels-modal-inner"
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 20 }}
            >
              <button className="reels-modal-close" onClick={closeModal}>
                <X size={18} />
              </button>

              <div
                className="reels-modal-video-wrapper"
                onClick={addReaction}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <div className="reels-modal-phone">
                  <iframe
                    ref={modalIframeRef}
                    className="reels-modal-video"
                    src={`${videos[activeIndex].src}?autoplay=1&mute=1&controls=1&enablejsapi=1`}
                    title="Expanded video"
                  />
                  <div className="reels-progress-bar">
                    <div className="reels-progress-fill" />
                  </div>
                </div>

                <div className="reels-reaction-layer">
                  <AnimatePresence>
                    {reactions.map((r) => (
                      <motion.span
                        key={r.id}
                        className="reaction-emoji"
                        initial={{ opacity: 0, y: 20, x: r.x }}
                        animate={{ opacity: 1, y: -60 }}
                        exit={{ opacity: 0 }}
                      >
                        {r.emoji}
                      </motion.span>
                    ))}
                  </AnimatePresence>
                </div>
              </div>

              <div className="reels-modal-meta">
                <div>
                  <div className="reels-modal-title">
                    {videos[activeIndex].title}
                  </div>
                  <div className="reels-modal-subtitle">
                    {videos[activeIndex].subtitle}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
